import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Search, Sparkles, X, Loader2 } from "lucide-react";

export default function SearchBar({ onClose }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [aiResult, setAiResult] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const inputRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    inputRef.current?.focus();
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const t = setTimeout(() => {
      api.post("/search", { query: q }).then((r) => setResults(r.data.topics || []));
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  const generateAI = async () => {
    if (!q.trim()) return;
    setAiLoading(true);
    setAiResult(null);
    try {
      const r = await api.post("/generate-analogy", { topic: q });
      setAiResult(r.data);
    } catch (e) {
      setAiResult({ error: "Could not generate analogy. Try a different query." });
    } finally { setAiLoading(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-24 px-4"
      style={{ background: "rgba(28,33,31,0.4)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="search-overlay"
    >
      <div
        className="w-full max-w-2xl pm-card p-6 pop-in flex flex-col"
        style={{ maxHeight: "calc(100vh - 8rem)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 pb-3 border-b shrink-0" style={{ borderColor: "var(--border)" }}>
          <Search size={20} style={{ color: "var(--text-muted)" }} />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && aiResult === null && generateAI()}
            placeholder="Type any topic — e.g. mitochondria, SN1, entropy…"
            className="flex-1 bg-transparent outline-none text-lg font-medium"
            data-testid="search-input"
          />
          <button onClick={onClose} data-testid="close-search-btn">
            <X size={18} style={{ color: "var(--text-muted)" }} />
          </button>
        </div>

        {/* Scrollable results region so long AI cards don't overflow the viewport */}
        <div className="overflow-y-auto flex-1 -mx-6 px-6" data-testid="search-scroll-region">

        {/* Existing matches */}
        {results.length > 0 && (
          <div className="mt-4 space-y-1" data-testid="search-results">
            <div className="text-xs uppercase tracking-widest font-semibold pb-2" style={{ color: "var(--text-muted)" }}>
              Existing topics
            </div>
            {results.map((t) => (
              <button
                key={t.id}
                onClick={() => { nav(`/app/topic/${t.id}`); onClose(); }}
                className="w-full text-left px-3 py-2.5 rounded-xl hover:bg-[var(--muted)] flex items-center gap-3"
                data-testid={`search-result-${t.id}`}
              >
                <div className="w-8 h-8 rounded-lg flex items-center justify-center text-sm" style={{ background: "var(--hl-green)" }}>
                  ✦
                </div>
                <div>
                  <div className="font-medium">{t.name}</div>
                  <div className="text-xs" style={{ color: "var(--text-muted)" }}>{t.category}</div>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* AI Analogy trigger */}
        {q.trim() && !aiResult && (
          <div className="mt-4">
            <button
              onClick={generateAI}
              disabled={aiLoading}
              className="pm-btn-accent w-full flex items-center justify-center gap-2"
              data-testid="generate-ai-analogy-btn"
            >
              {aiLoading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
              {aiLoading ? "Cooking up an analogy…" : `Generate analogy for "${q}"`}
            </button>
          </div>
        )}

        {/* AI Result */}
        {aiResult && !aiResult.error && (
          <div className="mt-5 pm-card p-5" data-testid="ai-analogy-result" style={{ background: "var(--hl-green)" }}>
            {aiResult.image_url && (
              <img
                src={aiResult.image_url}
                alt={aiResult.topic}
                className="w-full max-h-64 object-cover rounded-xl mb-4"
                data-testid="ai-analogy-image"
              />
            )}
            <div className="font-display font-bold text-xl mb-2">{aiResult.topic}</div>
            <p className="text-base leading-relaxed mb-3">{aiResult.analogy}</p>
            {aiResult.story && (
              <p className="text-sm leading-relaxed mb-3" style={{ color: "var(--text-secondary)" }}>{aiResult.story}</p>
            )}
            {aiResult.key_points?.length > 0 && (
              <ul className="space-y-1 text-sm">
                {aiResult.key_points.map((p, i) => (
                  <li key={i} className="flex gap-2"><span>•</span><span>{p}</span></li>
                ))}
              </ul>
            )}
          </div>
        )}
        {aiResult?.error && (
          <div className="mt-4 p-3 rounded-xl text-sm" style={{ background: "#fdecea", color: "var(--weakness)" }}>
            {aiResult.error}
          </div>
        )}
        </div>
      </div>
    </div>
  );
}
